import { Metadata } from '@cedarjs/web'
import { useState, useEffect } from 'react'
import {
  Grid2 as Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import ProductsList from './components/ProductsList'
import { useMutation, useQuery } from '@apollo/client'
import {
  CREATE_PRODUCT_RELATIONSHIP,
  DELETE_PRODUCT_RELATIONSHIP,
  GET_PRODUCT_ALTERNATES,
  GET_PRODUCTS_GIVEN_ALTERNATE,
  GET_PRODUCT,
} from './components/queries'
import toast from 'react-hot-toast'
import { useBreadcrumbs } from 'src/layouts/components/BreadcrumbsContext'
import { routes } from '@cedarjs/router'
import { useParams } from '@cedarjs/router'
import Button from 'src/components/MUI/Button'

// Relationship type constants
const RELATIONSHIP_TYPES = {
  ALTERNATE: 'ALTERNATE',
} as const

interface Product {
  id: string
  name: string
  partNumber: string
  description: string
  manufacturer: string
  type: string
  quantity: number
}

interface ProductRelationship {
  id: string
  product1Id: string
  createdAt: string
  product1: Product
}

interface ReverseProductRelationship {
  id: string
  product2Id: string
  createdAt: string
  product2: Product
}

const AddAlternateProductsPage = () => {
  const { orgName, productId } = useParams()

  useBreadcrumbs([
    { icon: 'inventory' },
    { label: 'Products', to: routes.productsList({ orgName }) },
    { label: 'Add Alternate Products' },
  ])

  // Query to get the product details if productId is provided
  const { data: productData, loading: loadingProduct } = useQuery(GET_PRODUCT, {
    variables: { id: productId || '' },
    skip: !productId,
  })

  const [selectedMainProduct, setSelectedMainProduct] =
    useState<Product | null>(null)
  const [selectedAlternateProduct, setSelectedAlternateProduct] =
    useState<Product | null>(null)
  const [createReverseRelationship, setCreateReverseRelationship] =
    useState<boolean>(false)

  // Set the selectedMainProduct when productData is loaded
  useEffect(() => {
    if (productData?.product) {
      setSelectedMainProduct(productData.product)
    }
  }, [productData])

  // Query to get existing alternates for the selected main product
  const {
    data: alternatesData,
    loading: loadingAlternates,
    refetch: refetchAlternates,
  } = useQuery(GET_PRODUCT_ALTERNATES, {
    variables: { productId: selectedMainProduct?.id || '' },
    skip: !selectedMainProduct?.id,
  })

  // Query to get products where the selected main product is an alternate
  const {
    data: reverseAlternatesData,
    loading: loadingReverseAlternates,
    refetch: refetchReverseAlternates,
  } = useQuery(GET_PRODUCTS_GIVEN_ALTERNATE, {
    variables: { productId: selectedMainProduct?.id || '' },
    skip: !selectedMainProduct?.id,
  })

  const [createProductRelationship, { loading: linkingProducts }] = useMutation(
    CREATE_PRODUCT_RELATIONSHIP,
    {
      onCompleted: async () => {
        toast.success('Alternate product successfully linked!')
        setSelectedAlternateProduct(null)
        // Refetch the alternates list to show the newly added alternate
        if (selectedMainProduct?.id) {
          await refetchAlternates()
          await refetchReverseAlternates()
        }
      },
      onError: (error) => {
        toast.error(error.message)
      },
    }
  )

  const [deleteProductRelationship, { loading: deletingRelationship }] =
    useMutation(DELETE_PRODUCT_RELATIONSHIP, {
      onCompleted: async () => {
        toast.success('Alternate product removed')
        // Refetch the alternates list to show the updated list
        if (selectedMainProduct?.id) {
          await refetchAlternates()
          await refetchReverseAlternates()
        }
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })

  const handleLinkProducts = async () => {
    if (!selectedMainProduct || !selectedAlternateProduct) return

    // Create the main relationship
    await createProductRelationship({
      variables: {
        input: {
          product1Id: selectedAlternateProduct.id,
          product2Id: selectedMainProduct.id,
          relationship: RELATIONSHIP_TYPES.ALTERNATE,
          createReverseRelationshipToo: createReverseRelationship,
        },
      },
    })
  }

  const handleDeleteRelationship = async (relationshipId: string) => {
    await deleteProductRelationship({
      variables: {
        id: relationshipId,
      },
    })
  }

  // Get list of existing alternate product IDs to exclude from selection
  const existingAlternateIds =
    alternatesData?.productRelationships?.map(
      (relationship: ProductRelationship) => relationship.product1.id
    ) || []

  const canLinkProducts =
    selectedMainProduct &&
    selectedAlternateProduct &&
    selectedMainProduct.id !== selectedAlternateProduct.id

  return (
    <>
      <Metadata title="Add Alternate Products" />
      <div className="p-4">
        <Grid container spacing={3}>
          {/* Main Product Selection */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              className="rounded-2xl border border-solid border-gray-200 p-4"
              elevation={0}
            >
              <div className="mb-4">
                <Typography variant="h6" gutterBottom>
                  Select Main Product
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose the product for which you want to add alternates
                </Typography>
                {selectedMainProduct && (
                  <Chip
                    label={`${selectedMainProduct.name} (${selectedMainProduct.partNumber})`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </div>
              <Box sx={{ height: '400px', overflow: 'hidden' }}>
                <ProductsList
                  selectedProduct={selectedMainProduct}
                  onProductSelect={setSelectedMainProduct}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Alternate Product Selection */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              className="rounded-2xl border border-solid border-gray-200 p-4"
              elevation={0}
            >
              <div className="mb-4">
                <Typography variant="h6" gutterBottom>
                  Select Alternate Product
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose a product that can be used as an alternative to the
                  main product
                </Typography>
                {selectedAlternateProduct && (
                  <Chip
                    label={`${selectedAlternateProduct.name} (${selectedAlternateProduct.partNumber})`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
              </div>
              <Box sx={{ height: '400px', overflow: 'hidden' }}>
                <ProductsList
                  selectedProduct={selectedAlternateProduct}
                  onProductSelect={setSelectedAlternateProduct}
                  excludeProductIds={[
                    selectedMainProduct?.id,
                    ...existingAlternateIds,
                  ]}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Action Button */}
          <Grid size={{ xs: 12 }}>
            <Paper
              className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-solid border-gray-200 p-4"
              elevation={0}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createReverseRelationship}
                    onChange={(e) =>
                      setCreateReverseRelationship(e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Also create reverse relationship (make main product an alternate of the alternate product)"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleLinkProducts}
                disabled={!canLinkProducts || linkingProducts}
                disabledTooltip="Please select a main product and an alternate product"
                startIcon={
                  linkingProducts ? <CircularProgress size={20} /> : null
                }
              >
                {linkingProducts ? 'Adding...' : 'Add as Alternate'}
              </Button>
            </Paper>
          </Grid>

          {/* Existing Alternates Section */}
          {selectedMainProduct && (
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={3}>
                {/* Products Where Main Product is an Alternate */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    className="rounded-2xl border border-solid border-gray-200 p-4"
                    elevation={0}
                  >
                    <div className="mb-4">
                      <Typography variant="h6" gutterBottom>
                        Parent Products
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Products that have
                        {selectedMainProduct && (
                          <Chip
                            label={`${selectedMainProduct.name} (${selectedMainProduct.partNumber})`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ mx: 1 }}
                          />
                        )}
                        as an alternate
                      </Typography>
                    </div>

                    {loadingReverseAlternates ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : reverseAlternatesData?.productRelationships?.length ===
                      0 ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        This product is not an alternate for any other products.
                      </Alert>
                    ) : (
                      <List
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                      >
                        {reverseAlternatesData?.productRelationships?.map(
                          (
                            relationship: ReverseProductRelationship,
                            index: number
                          ) => (
                            <div key={relationship.id}>
                              <ListItem
                                onClick={() =>
                                  setSelectedMainProduct(relationship.product2)
                                }
                                sx={{ cursor: 'pointer' }}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteRelationship(relationship.id)
                                    }}
                                    disabled={deletingRelationship}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  primary={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {relationship.product2.name}
                                      </Typography>
                                      <Chip
                                        label={relationship.product2.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box mt={0.5}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        <strong>Part Number:</strong>{' '}
                                        {relationship.product2.partNumber}
                                      </Typography>
                                      {relationship.product2.description && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          noWrap
                                        >
                                          {relationship.product2.description}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        <strong>Manufacturer:</strong>{' '}
                                        {relationship.product2.manufacturer ||
                                          'N/A'}{' '}
                                        | <strong>Qty:</strong>{' '}
                                        {relationship.product2.quantity}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {index <
                                reverseAlternatesData.productRelationships
                                  .length -
                                  1 && <Divider />}
                            </div>
                          )
                        )}
                      </List>
                    )}
                  </Paper>
                </Grid>

                {/* Existing Alternates */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    className="rounded-2xl border border-solid border-gray-200 p-4"
                    elevation={0}
                  >
                    <div className="mb-4">
                      <Typography variant="h6" gutterBottom>
                        Existing Alternates
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        For
                        {selectedMainProduct && (
                          <Chip
                            label={`${selectedMainProduct.name} (${selectedMainProduct.partNumber})`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ mx: 1 }}
                          />
                        )}
                      </Typography>
                    </div>

                    {loadingAlternates ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : alternatesData?.productRelationships?.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No alternates found for this product.
                      </Alert>
                    ) : (
                      <List
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                      >
                        {alternatesData?.productRelationships?.map(
                          (
                            relationship: ProductRelationship,
                            index: number
                          ) => (
                            <div key={relationship.id}>
                              <ListItem
                                onClick={() =>
                                  setSelectedMainProduct(relationship.product1)
                                }
                                sx={{ cursor: 'pointer' }}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteRelationship(relationship.id)
                                    }}
                                    disabled={deletingRelationship}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  primary={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {relationship.product1.name}
                                      </Typography>
                                      <Chip
                                        label={relationship.product1.type}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box mt={0.5}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        <strong>Part Number:</strong>{' '}
                                        {relationship.product1.partNumber}
                                      </Typography>
                                      {relationship.product1.description && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          noWrap
                                        >
                                          {relationship.product1.description}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        <strong>Manufacturer:</strong>{' '}
                                        {relationship.product1.manufacturer ||
                                          'N/A'}{' '}
                                        | <strong>Qty:</strong>{' '}
                                        {relationship.product1.quantity}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {index <
                                alternatesData.productRelationships.length -
                                  1 && <Divider />}
                            </div>
                          )
                        )}
                      </List>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  )
}

export default AddAlternateProductsPage
